<template>
  <!-- TKP-155 / tunesoft2 s.4 satır tablosu; TKP-156: salt okunur kullanım (tarihçe görüntüleme) — `readonly` silme tuşunu gizler -->
  <table class="table table-sm mb-0" data-test="draft-table">
    <thead>
      <tr>
        <th>{{ $t("approval.columns.no") }}</th>
        <th>{{ $t("approval.columns.operation") }}</th>
        <th>{{ $t("approval.columns.filter") }}</th>
        <th>{{ $t("approval.columns.before") }}</th>
        <th>{{ $t("approval.columns.after") }}</th>
        <th>{{ $t("approval.columns.author") }}</th>
        <th>{{ $t("approval.columns.updated-at") }}</th>
        <th>{{ $t("approval.columns.decided-by") }}</th>
        <th>{{ $t("approval.columns.decided-at") }}</th>
        <th v-if="decidable">{{ $t("approval.columns.decision") }}</th>
        <th v-if="!readonly"></th>
      </tr>
    </thead>
    <tbody>
      <!-- TKP-237 t.1–t.8: satır görünümü (İşlem / Filtre iki satır / Mevcut / Yeni) draft-view.js'den; toplu işlem (aynı batch) tek satır,
           seçim filtrede (TKP-244, hedef satırları açılmaz); karar ve kaldırma işlemin bütün satırlarına -->
      <template v-for="(g, index) in groups">
        <tr :key="g.key" data-test="edit" :data-batch="g.batch ? 'true' : 'false'">
          <td>{{ index + 1 }}</td>
          <td>
            {{ viewOf(g).operation }}<span v-if="!g.batch && g.edits[0].shadow" class="badge badge-light ml-1">{{ $t("approval.new-record") }}</span><span v-if="g.edits.some((e) => e.libraryNew)" class="badge badge-warning ml-1" data-test="library-new">{{ $t("approval.library-new") }}</span>
          </td>
          <td data-test="filter">
            <div data-test="filter-1">{{ viewOf(g).filter[0] }}</div>
            <div v-if="viewOf(g).filter[1]" class="text-muted" data-test="filter-2">{{ viewOf(g).filter[1] }}</div>
            <!-- TKP-244: toplu işlemde "Ayrıntı" (hedef başına satır) yok; filtreye sığmayan seçim (veri – ilgili kişi) düz metin olarak açılır -->
            <template v-if="clipped(g)">
              <a href="javascript:;" class="small" data-test="selection-toggle" @click="toggle(g.key)"><i class="bx" :class="open[g.key] ? 'bx-chevron-down' : 'bx-chevron-right'"></i> {{ $t("approval.view.selection", { count: viewOf(g).selection.length }) }}</a>
              <div v-if="open[g.key]" class="small text-muted" data-test="selection-list"><div v-for="s in viewOf(g).selection" :key="g.key + s">{{ s }}</div></div>
            </template>
            <!-- TKP-157: düzenleme isteği (yazar okur), birim ön kararı (TKP-134), ret (TKP-237 o.5: reddeden kişi + gerekçe) -->
            <div v-for="e in notes(g, 'request')" :key="'r' + e._id" class="small text-warning" data-test="edit-request">{{ $t("approval.row.requested", { name: e.editRequestedByName || "—" }) }}: {{ e.editRequest.reason }}</div>
            <div v-for="e in notes(g, 'pre')" :key="'p' + e._id" class="small text-info" data-test="pre-decision">{{ $t(`approval.pre-decision.${e.preDecision.kind}`, { name: e.preDecidedByName || "—" }) }}<span v-if="e.preDecision.reason"> · {{ e.preDecision.reason }}</span></div>
            <!-- TKP-241 p.5: yazılamayan satırın gerekçesi (ör. aynı isimde varlık) tarihçede de görünür -->
            <div v-for="e in notes(g, 'failed')" :key="'f' + e._id" class="small text-warning" data-test="failed-reason">{{ $t("approval.row.failed-reason") }}: {{ failedReason(e) }}</div>
            <div v-for="e in notes(g, 'reject')" :key="'x' + e._id" class="small text-danger" data-test="reject-reason">{{ $t("approval.row.rejected-by", { name: e.decidedByName || "—" }) }}<span v-if="e.decision.reason">: {{ e.decision.reason }}</span></div>
          </td>
          <!-- TKP-240 b.10: uzun değer kısaltılır, "Ayrıntı" tamamını pencerede gösterir -->
          <td data-test="before"><div v-for="v in viewOf(g).before" :key="'b' + v">{{ short(v) }}<a v-if="isLong(v)" href="javascript:;" class="small ml-1" data-test="value-detail" @click="showFull(v)">{{ $t("approval.view.value-detail") }}</a></div><span v-if="!viewOf(g).before.length">—</span></td>
          <td data-test="after"><div v-for="v in viewOf(g).after" :key="'a' + v"><strong>{{ short(v) }}</strong><a v-if="isLong(v)" href="javascript:;" class="small ml-1" data-test="value-detail" @click="showFull(v)">{{ $t("approval.view.value-detail") }}</a></div><span v-if="!viewOf(g).after.length">—</span></td>
          <td>{{ g.edits[0].authorName || "—" }}</td>
          <td><small>{{ lastUpdate(g) | date(true) }}</small></td>
          <!-- TKP-251: yazılamayan satırda Onay Veren Kişi alanı "Yazılamadı" (gerekçe filtrede); onaylayanın adı altında -->
          <td v-if="statusOf(g) === 'FAILED' && decisionOf(g)" data-test="decided-by"><span class="badge badge-warning" data-test="decided-by-failed">{{ $t("approval.row.failed") }}</span><div v-if="decisionOf(g).decidedByName" class="small text-muted">{{ decisionOf(g).decidedByName }}</div></td>
          <td v-else data-test="decided-by">{{ decisionOf(g) ? decisionOf(g).decidedByName || "✓" : "—" }}</td>
          <td><small>{{ decisionOf(g) && decisionOf(g).decision.at ? $options.filters.date(decisionOf(g).decision.at, true) : "—" }}</small></td>
          <!-- TKP-157 / tunesoft2 s.7 · TKP-237 o.1–o.3: Onayla (envantere), Düzenleme İste (açıklama), Reddet (gerekçe) -->
          <td v-if="decidable" class="text-nowrap">
            <template v-if="pendingOf(g).length">
              <a href="javascript:;" class="text-success mr-2" data-test="row-approve" v-b-tooltip.hover :title="$t('approval.row.approve')" @click="decide(g, 'APPROVE')"><i class="bx bx-check font-size-20"></i></a>
              <a href="javascript:;" class="text-warning mr-2" data-test="row-request" v-b-tooltip.hover :title="$t('approval.row.request')" @click="decide(g, 'EDIT_REQUEST')"><i class="bx bx-pencil font-size-18"></i></a>
              <a href="javascript:;" class="text-danger" data-test="row-reject" v-b-tooltip.hover :title="$t('approval.row.reject')" @click="decide(g, 'REJECT')"><i class="bx bx-x font-size-20"></i></a>
            </template>
            <!-- TKP-237 o.5: karar rozeti duruma göre — reddedilen satır "Uygulandı" görünüyordu -->
            <span v-else-if="statusOf(g) === 'REJECTED'" class="badge badge-danger" data-test="row-rejected">{{ $t("approval.row.rejected") }}</span>
            <span v-else-if="statusOf(g) === 'FAILED'" class="badge badge-warning" data-test="row-failed">{{ $t("approval.row.failed") }}</span>
            <span v-else class="badge badge-success" data-test="row-applied">{{ $t("approval.row.applied") }}</span>
          </td>
          <td v-if="!readonly" class="text-right">
            <!-- TKP-190 / md.100: onaylanıp envantere yazılmış (APPLIED) satır kaldırılamaz — rozet kalır, bağlantı sunulmaz -->
            <b-button v-if="removable && g.edits.some((e) => e.status !== 'APPLIED')" size="sm" variant="link" :disabled="busy" data-test="remove-edit" @click="remove(g)">{{ $t("approval.remove-edit") }}</b-button>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
const VALUE_LIMIT = 120; // TKP-240 b.10
import { fieldLabelKey, filterLine, summarize } from "./approval";
import { batchView, clipSelection, editView, groupEdits } from "./draft-view"; // TKP-237, TKP-244

export default {
  name: "draft-rows-table",
  props: { edits: { type: Array, default: () => [] }, readonly: { type: Boolean, default: false }, removable: { type: Boolean, default: false }, busy: { type: Boolean, default: false }, decidable: { type: Boolean, default: false } },
  data() {
    return { open: {} };
  },
  computed: {
    groups() {
      return groupEdits(this.edits);
    },
    /** TKP-244: grup görünümleri bir kez (kalabalık işlemde seçim yüzlerce satırdan çıkarılır; hücre başına yeniden hesaplanmaz). */
    views() {
      return Object.fromEntries(this.groups.map((g) => [g.key, g.batch ? batchView(g.batch, g.edits, this.ctx()) : editView(g.edits[0], this.ctx())]));
    },
  },
  methods: {
    /** TKP-240 b.10: değer hücresi kısaltması (VALUE_LIMIT karakter) ve tam metin penceresi. */
    isLong(v) {
      return String(v || "").length > VALUE_LIMIT;
    },
    short(v) {
      const text = String(v == null ? "" : v);
      return text.length > VALUE_LIMIT ? text.slice(0, VALUE_LIMIT).trimEnd() + "…" : text;
    },
    showFull(v) {
      const h = this.$createElement;
      return this.$bvModal.msgBoxOk(h("div", { style: "white-space: pre-wrap; word-break: break-word;", attrs: { "data-test": "value-full" } }, String(v)), { title: this.$t("approval.view.value-detail-title"), size: "lg", okTitle: this.$t("common.close"), centered: true });
    },
    /** TKP-237 o.5: onaylanmış ya da reddedilmiş satır — karar veren ve zaman sütunları dolar. */
    decided(edit) {
      return !!(edit.decision && (edit.decision.kind === "APPROVE" || edit.decision.kind === "REJECT"));
    },
    ctx() {
      const translate = this.$options.filters.translate;
      const tr = (v) => (typeof v === "string" ? v : !v ? "" : (translate && translate(v)) || v.tr || v.en || v.de || "");
      return { t: (k, p) => this.$t(k, p), tr };
    },
    viewOf(g) {
      return this.views[g.key];
    },
    /** TKP-244: seçim filtre satırına sığmadı mı (tamamı açılır). */
    clipped(g) {
      const v = this.viewOf(g);
      return !!(v.selection && v.selection.length && clipSelection(v.selection) !== v.selection.join("; "));
    },
    toggle(key) {
      this.$set(this.open, key, !this.open[key]);
    },
    pendingOf(g) {
      return g.edits.filter((e) => this.isPending(e));
    },
    /** Grubun kararı: ilk karar verilmiş satır (aynı işlemin satırları birlikte karara bağlanır). */
    decisionOf(g) {
      return g.edits.find((e) => this.decided(e)) || null;
    },
    statusOf(g) {
      const s = g.edits.map((e) => e.status);
      return s.includes("REJECTED") ? "REJECTED" : s.includes("FAILED") ? "FAILED" : "APPLIED";
    },
    lastUpdate(g) {
      return g.edits.map((e) => e.updatedAt).filter(Boolean).sort().slice(-1)[0];
    },
    /** Notlar (düzenleme isteği / ön karar / ret) — grupta aynı not bir kez. */
    /** TKP-241 p.5: yazılamama gerekçesi — onaylayanın paketinde FAILED satır, yazara dönmüşse `returnedFrom` görüntüsü. */
    failedReason(e) {
      if (e.status === "FAILED") return (e.decision && e.decision.reason) || "";
      const r = e.returnedFrom;
      return r && r.status === "FAILED" ? (r.decision && r.decision.reason) || "" : "";
    },
    notes(g, kind) {
      const has = { request: (e) => e.editRequest && e.editRequest.reason, pre: (e) => e.preDecision && e.preDecision.kind, reject: (e) => e.decision && e.decision.kind === "REJECT", failed: (e) => !!this.failedReason(e) }[kind];
      const seen = new Set();
      return g.edits.filter(has).filter((e) => {
        const k = kind === "request" ? e.editRequest.reason : kind === "pre" ? `${e.preDecision.kind}|${e.preDecision.reason || ""}` : kind === "failed" ? this.failedReason(e) : `${e.decidedByName}|${e.decision.reason || ""}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
    /** Grubun bekleyen bütün satırları için tek karar (eski olay biçimi `edit` de korunur). */
    decide(g, decision) {
      const edits = this.pendingOf(g);
      this.$emit("decide", { edit: edits[0], edits, decision });
    },
    remove(g) {
      const edits = g.edits.filter((e) => e.status !== "APPLIED");
      if (g.batch) this.$emit("remove-many", edits);
      else this.$emit("remove", edits[0]);
    },
    /** Karar verilebilir satır: bekleyen (APPLIED / REJECTED / FAILED değil). */
    isPending(edit) {
      return !edit.status || edit.status === "PENDING";
    },
    filterLine,
    summarize,
    /** Alanın insan-okur adı; çeviri yoksa ham alan adı (yeni alanlar sessizce kaybolmasın). */
    fieldLabel(field) {
      const key = fieldLabelKey(field);
      return this.$te && this.$te(key) ? this.$t(key) : field;
    },
  },
};
</script>
